/**
 * One player station (M6 plan P34, spec §7.2, §8 "Station management"): construction progress, production lines,
 * stock, the price policy with per-ware limits, trade permissions, the next upgrade and the manager. Thin: the
 * rules are `stationsModel.ts`.
 */
import type { ReactNode } from 'react'
import type { PlayerStationView, TradePermissionName } from '../../strategic/types'
import type { WareId } from '../../strategic/wares'
import { FocusButton } from '../focus'
import { formatCr } from '../format'
import { wareName } from '../strategic/names'
import {
  constructionModel,
  permissionLabel,
  policyBlock,
  policyLabel,
  stationEarnings,
  typeLabel,
  upgradeModel,
  type ManagerChoice,
  type PolicyDraft,
} from './stationsModel'

export interface StationManageProps {
  station: PlayerStationView
  policy: PolicyDraft
  permission: TradePermissionName
  managers: readonly ManagerChoice[]
  busy: boolean
  status: string | null
  systemName(id: string): string
  onBack(): void
  onPolicy(delta: 1 | -1): void
  onLimit(ware: WareId, side: 'min' | 'max', delta: 1 | -1): void
  onSavePolicy(): void
  onPermission(delta: 1 | -1): void
  onSavePermission(): void
  onUpgrade(): void
  onManager(captainId: string | null): void
}

function limitText(n: number): string {
  return n > 0 ? formatCr(n) : 'none'
}

export function StationManage(p: StationManageProps): ReactNode {
  const s = p.station
  const c = constructionModel(s)
  const up = upgradeModel(s)
  const pBlock = policyBlock(p.policy)
  let i = 0
  return (
    <div className="sui fp-panel corp-panel" data-testid="station-manage">
      <div className="corp-head">
        <FocusButton group="corp" index={i++} className="ui-btn--small" testId="manage-back" onActivate={p.onBack}>
          ← Stations
        </FocusButton>
        <h3 className="corp-title" data-testid="manage-name">
          {s.name}
        </h3>
      </div>
      <p className="fp-meta">
        {typeLabel(s.type)} · level {s.level} · {p.systemName(s.systemId)} · {stationEarnings(s)}
      </p>

      {c !== null ? (
        <>
          <h4 className="corp-sub">Construction (level {s.targetLevel})</h4>
          <p className="fp-meta" data-testid="manage-construction">
            {c.stage} · {Math.round(c.supplied * 100)}% supplied · {c.days} · auto-buy {s.autoBuy ? 'on' : 'off'}
          </p>
          {c.missing.length > 0 ? <p className="fp-meta">Still needs {c.missing.map((m) => `${Math.ceil(m.tonnes)} t ${wareName(m.ware)}`).join(', ')}.</p> : null}
        </>
      ) : null}

      {s.station !== null ? (
        <>
          <h4 className="corp-sub">Production</h4>
          {s.station.lines.length === 0 ? (
            <p className="sui-empty">No production lines.</p>
          ) : (
            <ul className="corp-list" data-testid="manage-lines">
              {s.station.lines.map((l, k) => (
                <li key={`${l.recipe}-${k}`}>
                  {l.recipe} · {l.status}
                  {l.missing !== null ? ` (needs ${wareName(l.missing)})` : ''} · {Math.round(l.progress * 100)}%
                </li>
              ))}
            </ul>
          )}
          <h4 className="corp-sub">Stock</h4>
          <table className="corp-pnl" data-testid="manage-stock">
            <tbody>
              {s.station.wares.map((w) => (
                <tr key={w.ware}>
                  <td>{wareName(w.ware)}</td>
                  <td className="ui-num">
                    {Math.round(w.stock)} / {Math.round(w.capacity)} t
                  </td>
                  <td className="ui-num">{w.buyUnit !== null ? `sells ${formatCr(w.buyUnit)}` : ''}</td>
                  <td className="ui-num">{w.sellUnit !== null ? `buys ${formatCr(w.sellUnit)}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      <h4 className="corp-sub">Price policy</h4>
      <div className="corp-fields">
        <FocusButton group="corp" index={i++} className="ui-btn--small" testId="manage-policy" disabled={p.busy} onActivate={() => p.onPolicy(1)} onAdjust={p.onPolicy}>
          ◂ {policyLabel(p.policy.policy)} ▸
        </FocusButton>
      </div>
      {p.policy.policy === 'manual' ? (
        <ul className="corp-list" data-testid="manage-limits">
          {p.policy.limits.map((l) => (
            <li key={l.ware} className="corp-row">
              <span>{wareName(l.ware)}</span>
              <FocusButton group="corp" index={i++} className="ui-btn--small" testId={`manage-min-${l.ware}`} onActivate={() => p.onLimit(l.ware, 'min', 1)} onAdjust={(d) => p.onLimit(l.ware, 'min', d)}>
                Min ◂ {limitText(l.min)} ▸
              </FocusButton>
              <FocusButton group="corp" index={i++} className="ui-btn--small" testId={`manage-max-${l.ware}`} onActivate={() => p.onLimit(l.ware, 'max', 1)} onAdjust={(d) => p.onLimit(l.ware, 'max', d)}>
                Max ◂ {limitText(l.max)} ▸
              </FocusButton>
            </li>
          ))}
        </ul>
      ) : null}
      <FocusButton
        group="corp"
        index={i++}
        className="ui-btn--small"
        testId="manage-save-policy"
        disabled={p.busy || pBlock !== ''}
        title={pBlock === '' ? undefined : pBlock}
        onActivate={p.onSavePolicy}
      >
        Save price policy
      </FocusButton>

      <h4 className="corp-sub">Trade permissions</h4>
      <div className="corp-fields">
        <FocusButton group="corp" index={i++} className="ui-btn--small" testId="manage-permission" disabled={p.busy} onActivate={() => p.onPermission(1)} onAdjust={p.onPermission}>
          ◂ {permissionLabel(p.permission)} ▸
        </FocusButton>
        <FocusButton group="corp" index={i++} className="ui-btn--small" testId="manage-save-permission" disabled={p.busy || p.permission === s.permission} onActivate={p.onSavePermission}>
          Apply
        </FocusButton>
      </div>

      <h4 className="corp-sub">Upgrade</h4>
      {s.upgrade !== null ? (
        <p className="fp-meta" data-testid="manage-upgrade-cost">
          Level {s.upgrade.level}: {(Object.keys(s.upgrade.cost) as WareId[]).map((w) => `${s.upgrade?.cost[w] ?? 0} t ${wareName(w)}`).join(', ')}; {s.upgrade.days} days.
        </p>
      ) : null}
      <FocusButton
        group="corp"
        index={i++}
        className="ui-btn--small"
        testId="manage-upgrade"
        disabled={p.busy || !up.canUpgrade}
        title={up.canUpgrade ? undefined : up.reason}
        onActivate={p.onUpgrade}
      >
        {up.canUpgrade ? 'Start the upgrade' : up.reason}
      </FocusButton>

      <h4 className="corp-sub">Manager</h4>
      <p className="fp-meta" data-testid="manage-manager">
        {s.manager !== null
          ? `${s.manager.name} · Trade ${s.manager.skills.trade}, Leadership ${s.manager.skills.leadership}`
          : 'No manager. A hired captain with Trade and Leadership speeds the lines up.'}
      </p>
      {p.managers.length === 0 ? (
        <p className="sui-empty">No hired captain is free to manage.</p>
      ) : (
        <ul className="corp-list" data-testid="manage-managers">
          {p.managers.map((m) => (
            <li key={m.captainId ?? 'none'}>
              <FocusButton
                group="corp"
                index={i++}
                className="ui-btn--small"
                testId={`manage-assign-${m.captainId ?? 'none'}`}
                disabled={p.busy}
                onActivate={() => p.onManager(m.captainId)}
              >
                {m.label}
              </FocusButton>
            </li>
          ))}
        </ul>
      )}
      {p.status !== null ? (
        <p className="fp-status" role="status" data-testid="manage-status">
          {p.status}
        </p>
      ) : null}
    </div>
  )
}
